import type {
	MarketingTask,
	MarketingTaskListItem,
	MarketingTaskUpsertRequest,
} from "@/shared/api/generated/types/marketing"
import {
	MarketingTaskStatus,
	MarketingTaskType,
} from "@/shared/api/generated/types/marketing"

import { createInitialMarketingTasks } from "./marketing-task-full-fixtures"

const STORAGE_KEY = "admin-ui.msw.marketing-tasks.v5"

function canUseSessionStorage(): boolean {
	try {
		return typeof sessionStorage !== "undefined"
	} catch {
		return false
	}
}

function restoreIdCounter(taskMap: Map<string, MarketingTask>): number {
	let max = 100
	for (const id of taskMap.keys()) {
		const mailingMatch = id.match(/^task-mailing-(\d+)$/)
		const reportMatch = id.match(/^task-report-(\d+)$/)
		const match = mailingMatch ?? reportMatch
		if (match) {
			max = Math.max(max, Number(match[1]))
		}
	}
	return max
}

function loadTasksFromStorage(): Map<string, MarketingTask> | null {
	if (!canUseSessionStorage()) {
		return null
	}

	try {
		const raw = sessionStorage.getItem(STORAGE_KEY)
		if (!raw) {
			return null
		}

		const parsed = JSON.parse(raw) as MarketingTask[]
		if (!Array.isArray(parsed)) {
			return null
		}

		return new Map(parsed.map((task) => [task.id, task]))
	} catch {
		return null
	}
}

function persistTasks(taskMap: Map<string, MarketingTask>): void {
	if (!canUseSessionStorage()) {
		return
	}

	sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...taskMap.values()]))
}

const storedTasks = loadTasksFromStorage()
const tasks =
	storedTasks ??
	new Map(createInitialMarketingTasks().map((task) => [task.id, task]))

let idCounter = restoreIdCounter(tasks)

function nextTaskId(type: MarketingTaskType): string {
	idCounter += 1
	const prefix =
		type === MarketingTaskType.report ? "task-report" : "task-mailing"
	return `${prefix}-${idCounter}`
}

function resolveTaskStatus(
	body: MarketingTaskUpsertRequest,
	existing?: MarketingTask,
): MarketingTaskStatus {
	if (body.status) {
		return body.status
	}

	if (existing?.status === MarketingTaskStatus.draft) {
		return MarketingTaskStatus.draft
	}

	if (!body.active) {
		if (
			body.schedule.mode === "once" &&
			!body.schedule.once?.at &&
			!existing?.lastRunAt
		) {
			return MarketingTaskStatus.draft
		}

		return MarketingTaskStatus.paused
	}

	return MarketingTaskStatus.active
}

function toListItem(task: MarketingTask): MarketingTaskListItem {
	return {
		id: task.id,
		type: task.type,
		name: task.name,
		description: task.description,
		status: task.status,
		active: task.active,
		scheduleKind: task.schedule.mode,
		scheduleLabel:
			task.scheduleLabel ??
			(task.schedule.mode === "recurring" ? "По расписанию" : "Разовая"),
		nextRunAt: task.nextRunAt ?? null,
		lastRunAt: task.lastRunAt ?? null,
	}
}

function upsertTimestamps(
	task: MarketingTask,
	partial?: Partial<MarketingTask>,
): MarketingTask {
	const now = new Date().toISOString()
	return {
		...task,
		...partial,
		updatedAt: now,
	}
}

export function getMarketingTaskById(
	taskId: string,
): MarketingTask | undefined {
	return tasks.get(taskId)
}

export function listMarketingTaskItems(
	type: MarketingTaskType | null,
	scheduleFilter?: "all" | "once" | "recurring" | null,
): MarketingTaskListItem[] {
	let items = [...tasks.values()].map(toListItem)

	if (type) {
		items = items.filter((task) => task.type === type)
	}

	if (scheduleFilter && scheduleFilter !== "all") {
		items = items.filter((task) => task.scheduleKind === scheduleFilter)
	}

	return items
}

export function createMarketingTaskInStore(
	body: MarketingTaskUpsertRequest,
): MarketingTask {
	const now = new Date().toISOString()
	const task: MarketingTask = {
		...body,
		id: nextTaskId(body.type),
		status: resolveTaskStatus(body),
		nextRunAt:
			body.schedule.mode === "once"
				? (body.schedule.once?.at ?? null)
				: "2026-07-01T07:00:00.000Z",
		lastRunAt: null,
		createdAt: now,
		updatedAt: now,
	}
	tasks.set(task.id, task)
	persistTasks(tasks)
	return task
}

export function updateMarketingTaskInStore(
	taskId: string,
	body: MarketingTaskUpsertRequest,
): MarketingTask | undefined {
	const existing = tasks.get(taskId)
	if (!existing) {
		return undefined
	}

	const task = upsertTimestamps(existing, {
		...body,
		id: taskId,
		status: resolveTaskStatus(body, existing),
		nextRunAt:
			body.schedule.mode === "once"
				? (body.schedule.once?.at ?? null)
				: existing.nextRunAt,
		lastRunAt: existing.lastRunAt,
		createdAt: existing.createdAt,
	})
	tasks.set(taskId, task)
	persistTasks(tasks)
	return task
}

export function duplicateMarketingTaskInStore(
	taskId: string,
): MarketingTask | undefined {
	const source = tasks.get(taskId)
	if (!source) {
		return undefined
	}

	const now = new Date().toISOString()
	const copy: MarketingTask = {
		...structuredClone(source),
		id: nextTaskId(source.type),
		name: `${source.name} (копия)`,
		active: false,
		status: MarketingTaskStatus.paused,
		lastRunAt: null,
		nextRunAt: source.schedule.mode === "once" ? null : source.nextRunAt,
		createdAt: now,
		updatedAt: now,
	}
	tasks.set(copy.id, copy)
	persistTasks(tasks)
	return copy
}

export function deleteMarketingTaskInStore(taskId: string): boolean {
	const deleted = tasks.delete(taskId)
	if (deleted) {
		persistTasks(tasks)
	}
	return deleted
}

export function toggleMarketingTaskInStore(
	taskId: string,
	active: boolean,
): MarketingTaskListItem | undefined {
	const existing = tasks.get(taskId)
	if (!existing) {
		return undefined
	}

	const task = upsertTimestamps(existing, {
		active,
		status: active ? MarketingTaskStatus.active : MarketingTaskStatus.paused,
	})
	tasks.set(taskId, task)
	persistTasks(tasks)
	return toListItem(task)
}
